import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Link,
  Modal,
  Typography,
} from '@mui/material';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import StarIcon from '@mui/icons-material/Star';
import {
  COLOR_RED,
  DEMO_INDIVIDUAL_HOST_DATA,
  exchangeRates,
  TOP_HOST_TOOLTIP_MAP,
} from 'src/utils/const';
import {
  formatCurrency,
  convertCurrency,
  formatUrl,
  makeDownloadableBlob,
} from 'src/utils/functions';
import { VerifiedTag } from 'src/App/Dashboards/Market/Components/Tags';
import StatsCardSideModal from 'src/App/Dashboards/Market/Components/Cards/StatsCardSideModal';
import { User } from 'src/interfaces/iuser';
import { useSelector } from 'react-redux';
import { Search } from 'src/interfaces/idashboard';

const convertDetailedDataToCSV = (dataArray: any[], currency: string): string => {
  const headers = [
    'Airbnb URL',
    'Name',
    'Listings',
    'Avg Rating',
    `Avg Revenue (${currency})`,
    'Avg Occupancy Rate (%)',
    `Avg Daily Rate (${currency})`,
    'Website',
    'Verification Status',
    'Most Common Bedroom',
    'Inventory Growth (%)',
  ];

  const rows = dataArray.map(item => {
    return [
      `https://www.airbnb.com/users/show/${item.id}`,
      `"${item.name}"`,
      item.listings,
      item.rating,
      item.revenue,
      item.occupancy_rate,
      item.adr,
      item.website_url,
      item.verification_status,
      item.most_common_bedroom,
      item.inventory_growth,
    ];
  });

  return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
};

interface HostModalProps {
  open: boolean;
  onClose: () => void;
  host?: any;
  hosts?: any[];
  currency: string;
  isMobileScreen: boolean;
  setHost: (host: any) => void;
  handleOnSampleClick: any;
  user: User;
  getTableComponent: () => JSX.Element;
  returnModalBodyOnly?: boolean;
  isAccessDenied?: boolean;
  handleUnlockTopHosts: () => void;
  isLoading?: boolean;
}

const HostModal: React.FC<HostModalProps> = ({
  open,
  onClose,
  host,
  hosts,
  currency,
  isMobileScreen,
  setHost,
  handleOnSampleClick,
  user,
  getTableComponent,
  returnModalBodyOnly,
  isAccessDenied,
  handleUnlockTopHosts,
  isLoading,
}) => {
  // currentHost stores the host whose details are being displayed.
  const [currentHost, setCurrentHost] = useState<any>(host);
  const searchObj: Search = useSelector((state: any) => state.dashboard.search.data);

  // Update currentHost when the prop "host" changes.
  useEffect(() => {
    setCurrentHost(host);
  }, [host]);

  const currencySymbol = exchangeRates[currency].symbol;

  // Container styles for the modal.
  const containerStyles = {
    background: '#fff',
    position: 'absolute' as 'absolute',
    top: '0',
    right: '0',
    height: '100dvh',
    overflow: 'auto',
    width: isMobileScreen ? '100vw' : host ? '60vw' : '90vw',
    maxWidth: 900,
    borderRadius: 4,
    boxShadow: 24,
    p: 4,
    outline: 'none',
    transition: 'width 0.25s ease-in-out',
  };

  // Config for the stats section based on currentHost.
  const statsConfig = currentHost
    ? [
        {
          title: 'Total Listings',
          value: formatCurrency(String(currentHost.listings)),
          isLocked: false,
          tooltipContent: TOP_HOST_TOOLTIP_MAP.listings,
        },
        {
          title: 'Average Rating',
          value: currentHost.rating + '★',
          isLocked: currentHost.is_locked,
          tooltipContent: TOP_HOST_TOOLTIP_MAP.avg_rating,
        },
        {
          title: 'Average Revenue',
          value:
            currencySymbol + formatCurrency(String(convertCurrency(currency, currentHost.revenue))),
          isLocked: currentHost.is_locked,
          tooltipContent: TOP_HOST_TOOLTIP_MAP.avg_revenue,
        },
        {
          title: 'Average Occupancy Rate',
          value: currentHost.occupancy_rate + '%',
          isLocked: currentHost.is_locked,
          tooltipContent: TOP_HOST_TOOLTIP_MAP.avg_occupancy_rate,
        },
        {
          title: 'Average Daily Rate',
          value:
            currencySymbol + formatCurrency(String(convertCurrency(currency, currentHost.adr))),
          isLocked: currentHost.is_locked,
          tooltipContent: TOP_HOST_TOOLTIP_MAP.avg_daily_rate,
        },
        {
          title: 'Most Common Bedroom',
          value: currentHost.most_common_bedroom,
          isLocked: currentHost.is_locked,
          tooltipContent: TOP_HOST_TOOLTIP_MAP.most_common_bedroom,
        },
        {
          title: 'Inventory Growth',
          value: currentHost.inventory_growth + '%',
          isLocked: currentHost.is_locked,
          tooltipContent: TOP_HOST_TOOLTIP_MAP.inventory_growth,
        },
      ]
    : [];

  const handleDownloadTopHosts = () => {
    if (!hosts) return;

    const csvData = convertDetailedDataToCSV(hosts, currency);
    const fileName = `top_hosts_in_${searchObj.name}_${new Date().toISOString().split('T')[0]}`;
    makeDownloadableBlob(csvData, fileName, true, true);
  };

  const HostListComponent = () => {
    if (!hosts) return null;

    return (
      <Grid item xs={12}>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          flexWrap="nowrap"
          sx={{ mb: '10px' }}
        >
          <Typography variant="h6">Top Airbnb Management Companies</Typography>
          {!returnModalBodyOnly && (
            <IconButton onClick={onClose}>
              <CloseRoundedIcon sx={{ cursor: 'pointer', color: COLOR_RED, fontSize: 40 }} />
            </IconButton>
          )}
        </Grid>

        <Grid
          container
          justifyContent="flex-end"
          alignItems="center"
          flexWrap="nowrap"
          sx={{ mb: '10px' }}
        >
          <Button
            variant="contained"
            sx={{ color: '#fff' }}
            endIcon={<DownloadIcon />}
            onClick={() => {
              if (isAccessDenied) {
                if (['expert', 'scale'].includes(user.subscriptionPlan)) {
                  handleUnlockTopHosts();
                } else {
                  handleOnSampleClick('expert');
                }
              } else {
                handleDownloadTopHosts();
              }
            }}
          >
            Export
          </Button>
        </Grid>

        {getTableComponent()}
      </Grid>
    );
  };

  const HostDetailsComponent = () => {
    const handleOnLockIconCick = () => {
      if (['expert', 'scale'].includes(user.subscriptionPlan)) {
        handleUnlockTopHosts && handleUnlockTopHosts();
      } else {
        handleOnSampleClick('expert');
      }
    };

    return (
      <Grid item xs={12}>
        <Grid container justifyContent="space-between" alignItems="center" flexWrap="nowrap">
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {currentHost.name}{' '}
            {currentHost.verification_status === 'verified' && (
              <VerifiedTag tooltipTitle="Airbtics verified host." />
            )}
            {currentHost.website_url && (
              <>
                <span
                  style={{
                    width: '2px',
                    height: '20px',
                    backgroundColor: '#000',
                    display: 'inline-block',
                    margin: '0 10px',
                  }}
                />
                <Link
                  href={formatUrl(currentHost.website_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '16px',
                    textDecoration: 'none',
                  }}
                >
                  Host Website
                  <OpenInNewRoundedIcon sx={{ ml: '4px', fontSize: 'inherit' }} />
                </Link>
              </>
            )}
          </Typography>
          {!returnModalBodyOnly && (
            <IconButton onClick={onClose}>
              <CloseRoundedIcon sx={{ cursor: 'pointer', color: COLOR_RED, fontSize: 40 }} />
            </IconButton>
          )}
        </Grid>

        <Grid container sx={{ mt: 2, gap: '20px' }}>
          {statsConfig.map((stat, idx) => (
            <StatsCardSideModal
              title={stat.title}
              value={stat.value}
              isDisabled={stat.isLocked}
              isListingRecentlyAdded={false}
              upgradeMessage={''}
              handleOnSampleClick={handleOnSampleClick}
              handleOnLockIconCick={handleOnLockIconCick}
              isMobileScreen={isMobileScreen}
              isAdrAnomaly={false}
              onClose={() => {}}
              setMarketUrlBack={() => {}}
              tooltipContent={stat.tooltipContent}
            />
          ))}
        </Grid>

        <Grid container sx={{ mt: 4, gap: '20px', position: 'relative' }}>
          <Typography variant="h6" sx={{ width: '100%' }}>
            Top reviews by guests
          </Typography>
          {currentHost.good_reviews &&
            JSON.parse(
              currentHost.good_reviews === 'locked'
                ? DEMO_INDIVIDUAL_HOST_DATA.good_reviews
                : currentHost.good_reviews
            ).map((review: any, index: number) => (
              <Grid
                item
                xs={12}
                key={index}
                sx={{
                  filter:
                    currentHost.is_locked || currentHost.good_reviews === 'locked'
                      ? 'blur(4px)'
                      : 'none',
                }}
              >
                <Card
                  sx={{
                    width: '95%',
                    p: 2,
                    borderRadius: 3,
                    boxShadow: 3,
                    mb: '12px',
                  }}
                >
                  <CardContent>
                    {/* Star Rating */}
                    <Box display="flex" alignItems="center" mb={1}>
                      {Array(review.rating)
                        .fill(0)
                        .map((_, i) => (
                          <StarIcon key={i} sx={{ color: '#FFA000', fontSize: 20 }} />
                        ))}
                    </Box>
                    {/* Review Text */}
                    <Typography variant="body1" sx={{ fontSize: 16, color: '#333' }}>
                      {review.comments.replace(/<br\/>/g, '\n')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}

          {currentHost.is_locked && (
            <Button
              variant="contained"
              onClick={() => {
                handleOnLockIconCick();
                onClose();
              }}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: '#fff',
              }}
            >
              Show Reviews
            </Button>
          )}
        </Grid>
      </Grid>
    );
  };

  const backButtonToHostList = () => {
    return (
      <Grid container justifyContent="flex-start" sx={{ mb: 0 }}>
        <Button
          size="small"
          onClick={() => setHost(null)}
          variant="contained"
          startIcon={<ArrowBackIcon />}
          sx={{ color: '#fff' }}
        >
          Back to Host List
        </Button>
      </Grid>
    );
  };

  const componentInRender = currentHost
    ? 'HostDetailsComponent'
    : hosts && hosts.length > 0
    ? 'HostListComponent'
    : null;

  const renderBackButton = hosts && hosts.length > 0 && currentHost;

  const ModalBody = (
    <Grid container sx={{ gap: '20px', width: '100%' }}>
      {isLoading && (
        <Grid container justifyContent="center" alignItems="center" height="100%">
          <CircularProgress size={60} />
        </Grid>
      )}

      {renderBackButton && backButtonToHostList()}
      {/* Host details */}
      {componentInRender === 'HostDetailsComponent' && HostDetailsComponent()}

      {/* Conditionally render the hosts list if hosts is provided and non-empty */}
      {componentInRender === 'HostListComponent' && HostListComponent()}
    </Grid>
  );

  if (returnModalBodyOnly) {
    return ModalBody;
  }

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Container sx={containerStyles} className={isMobileScreen ? 'safe-mobile-height' : ''}>
          {ModalBody}
        </Container>
      </Modal>
    </>
  );
};

export default HostModal;
